import React, { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Users2 } from "lucide-react";
import { useTeams } from "@/hooks/useTeams";

const NONE_VALUE = "__none__";

/**
 * Ekip seçici — birincil (zorunlu/opsiyonel) + yardımcı (opsiyonel).
 *
 * Props:
 *   roleSlug        : string (personelin role slug'ı — sadece bu role bağlı ekipler listelenir)
 *   primaryTeamId   : string | null
 *   secondaryTeamId : string | null
 *   onChange({ primary, secondary }) : iki alan tek call'da güncellenir
 *   disabled        : boolean
 *
 * Davranış:
 * - Migration 0066: her ekip TEK role bağlı. roleSlug verilmemişse veya
 *   o rol için aktif ekip yoksa **component null döner** (parent koşulsuz
 *   render edebilir).
 * - Yardımcı ekip dropdown'unda birincil ekip filtrelenir (aynı olamaz).
 * - Pasif (is_active=false) ekipler: mevcut atama varsa görünür (badge'de),
 *   ama yeni atama dropdown'larında listelenmez.
 */
const TeamPicker = ({
  roleSlug = null,
  primaryTeamId = null,
  secondaryTeamId = null,
  onChange,
  disabled = false,
}) => {
  const { activeTeams, defaultTeam, getTeamById, teamsLoading } = useTeams({
    activeOnly: false,
    roleSlug,
  });

  // Pasif olsa bile mevcut atanmış ekipler dropdown'da görünmeli.
  // NOT: Hook'lar koşulsuz çağrılmalı — erken return'lardan ÖNCE tanımlanır
  // (Rules of Hooks: aksi halde "Rendered fewer hooks than expected" hatası).
  const teamOptions = useMemo(() => {
    const set = new Map();
    for (const t of activeTeams) set.set(String(t.id), t);
    const pTeam = primaryTeamId ? getTeamById(primaryTeamId) : null;
    if (pTeam) set.set(String(pTeam.id), pTeam);
    const sTeam = secondaryTeamId ? getTeamById(secondaryTeamId) : null;
    if (sTeam) set.set(String(sTeam.id), sTeam);
    return Array.from(set.values()).sort((a, b) => {
      if (a.is_default && !b.is_default) return -1;
      if (!a.is_default && b.is_default) return 1;
      return String(a.display_name || "").localeCompare(
        String(b.display_name || ""),
        "tr",
      );
    });
  }, [activeTeams, primaryTeamId, secondaryTeamId, getTeamById]);

  const secondaryOptions = useMemo(
    () =>
      teamOptions.filter(
        (t) => !primaryTeamId || String(t.id) !== String(primaryTeamId),
      ),
    [teamOptions, primaryTeamId],
  );

  // roleSlug yoksa hiçbir şey render etme
  if (!roleSlug) return null;

  // Bu role bağlı hiçbir ekip yoksa ve mevcut atama da yoksa → render etme
  const hasAnyOption =
    activeTeams.length > 0 ||
    Boolean(primaryTeamId) ||
    Boolean(secondaryTeamId);
  if (!hasAnyOption && !teamsLoading) return null;

  const primaryTeam = primaryTeamId ? getTeamById(primaryTeamId) : null;
  const secondaryTeam = secondaryTeamId ? getTeamById(secondaryTeamId) : null;

  const handlePrimaryChange = (value) => {
    const newPrimary = value === NONE_VALUE ? null : value || null;
    // Birincil değiştiyse ve yardımcı aynıysa, yardımcıyı temizle
    const nextSecondary =
      newPrimary && String(newPrimary) === String(secondaryTeamId)
        ? null
        : secondaryTeamId || null;
    onChange?.({ primary: newPrimary, secondary: nextSecondary });
  };

  const handleSecondaryChange = (value) => {
    const newSecondary = value === NONE_VALUE ? null : value || null;
    onChange?.({ primary: primaryTeamId || null, secondary: newSecondary });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
        <Users2 className="h-4 w-4 text-emerald-700" />
        Ekip Atamaları
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Birincil Ekip</Label>
          <SearchableSelect
            value={primaryTeamId || NONE_VALUE}
            onChange={handlePrimaryChange}
            disabled={disabled || teamsLoading}
            allValue={NONE_VALUE}
            allLabel="— Yok —"
            searchPlaceholder="Ekip ara..."
            options={teamOptions.map((t) => ({
              id: t.id,
              name: `${t.display_name}${t.is_default ? " · default" : ""}${!t.is_active ? " · pasif" : ""}`,
            }))}
          />
          {primaryTeam && (
            <div className="mt-1">
              <Badge className="bg-emerald-100 text-emerald-900 border border-emerald-200 text-xs">
                {primaryTeam.display_name}
              </Badge>
            </div>
          )}
        </div>

        <div>
          <Label>Yardımcı Ekip (opsiyonel)</Label>
          <SearchableSelect
            value={secondaryTeamId || NONE_VALUE}
            onChange={handleSecondaryChange}
            disabled={disabled || teamsLoading || !primaryTeamId}
            allValue={NONE_VALUE}
            allLabel="— Yok —"
            searchPlaceholder="Ekip ara..."
            options={secondaryOptions.map((t) => ({
              id: t.id,
              name: `${t.display_name}${!t.is_active ? " · pasif" : ""}`,
            }))}
          />
          {secondaryTeam && (
            <div className="mt-1">
              <Badge className="bg-cyan-100 text-cyan-900 border border-cyan-200 text-xs">
                {secondaryTeam.display_name}
              </Badge>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamPicker;
